import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { computeGstFromInclusive, getGstStamp } from "@/tax/gst";
import { devLog } from "@/lib/log";
import { toast } from "sonner";
import { CheckCircle2, XCircle, AlertTriangle, Search, Loader2, Camera, Mic, Square } from "lucide-react";
import TaxDisclaimer from "@/components/tax/TaxDisclaimer";
import BiSelect from "@/components/BiSelect";

const VERDICT_META = {
  yes: {
    label: "Deductible",
    color: "#34D399",
    icon: CheckCircle2,
  },
  partial: {
    label: "Partial / conditional",
    color: "#FBBF24",
    icon: AlertTriangle,
  },
  conditional: {
    label: "Conditional",
    color: "#FBBF24",
    icon: AlertTriangle,
  },
  no: {
    label: "Not deductible",
    color: "#F87171",
    icon: XCircle,
  },
};

/**
 * Instant Deduction Checker — quick form, no upload needed. Feeds the same
 * fields as an Entry (supplier / desc / total / category) and shows a
 * verdict from the curated ATO ruleset.
 */
export default function DeductionCheckerCard() {
  const [form, setForm] = useState({
    supplier: "",
    description: "",
    total: 0,
    deduction_category: "materials",
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewImage, setPreviewImage] = useState(null); // blob URL of last snap
  const [transcript, setTranscript] = useState("");
  const [recording, setRecording] = useState(false);
  const photoInput = useRef(null);
  const recRef = useRef({ mr: null, chunks: [], stream: null });

  const applyPhotoResult = (data) => {
    const parsed = data.parsed || {};
    setForm({
      supplier: parsed.supplier || "",
      description: parsed.description || "",
      total: parseFloat(parsed.total || 0),
      deduction_category: parsed.deduction_category || form.deduction_category,
    });
    setResult(data.verdict || null);
  };

  const onPhoto = async (e) => {
    const raw = e.target.files?.[0];
    if (!raw) return;
    // Client-side resize (matches Capture + Attachments) — a fresh phone
    // photo can be 8 MB; the vision model doesn't need more than 1600px.
    const { resizeImage } = await import("@/lib/resizeImage");
    const file = await resizeImage(raw, { maxEdge: 1200, quality: 0.70 });
    if (previewImage) URL.revokeObjectURL(previewImage);
    setPreviewImage(URL.createObjectURL(file));
    setTranscript("");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/tax/deduction-check-photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      applyPhotoResult(data);
      toast.success("AI read the receipt — check the verdict below");
    } catch (err) {
      devLog.warn("photo deduction failed", err);
      toast.error("Couldn't read that receipt. Try a clearer photo.");
    } finally {
      setBusy(false);
      if (e.target) e.target.value = "";
    }
  };

  const startVoice = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      recRef.current = { mr, chunks: [], stream };
      mr.ondataavailable = (ev) => {
        if (ev.data.size > 0) recRef.current.chunks.push(ev.data);
      };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(recRef.current.chunks, { type: "audio/webm" });
        setBusy(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "deduction.webm");
          const { data } = await api.post("/tax/deduction-check-voice", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          setTranscript(data.transcript || "");
          if (previewImage) {
            URL.revokeObjectURL(previewImage);
            setPreviewImage(null);
          }
          applyPhotoResult(data);
          toast.success("AI heard you — check the verdict below");
        } catch (err) {
          devLog.warn("voice deduction failed", err);
          toast.error("Voice check failed. Try again.");
        } finally {
          setBusy(false);
          setRecording(false);
        }
      };
      mr.start();
      setRecording(true);
    } catch (err) {
      devLog.warn("mic denied", err);
      toast.error("Microphone permission required");
    }
  };

  const stopVoice = () => {
    recRef.current.mr?.stop();
  };

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tax/deduction-check", form);
      setResult(data);
    } catch (err) {
      devLog.warn("deduction check failed", err);
      toast.error("Check failed — try again");
    } finally {
      setBusy(false);
    }
  };

  const saveAsPending = async () => {
    setSaving(true);
    try {
      // ATO ruleset audit stamp — persisted alongside the entry so a
      // future audit can prove which ruleset produced the GST portion.
      const stamp = getGstStamp();
      await api.post("/entries", {
        supplier: form.supplier,
        description: form.description,
        total: Number(form.total || 0),
        // GST rate sourced from the 2026.1 ATO Knowledge Base (falls back
        // to the historical 10% if the KB is ever unavailable).
        gst: computeGstFromInclusive(Number(form.total || 0)),
        deduction_category: form.deduction_category,
        source: transcript ? "voice" : previewImage ? "photo" : "manual",
        raw_transcript: transcript || "",
        entry_date: new Date().toISOString().slice(0, 10),
        status: "pending",
        ato_ruleset_version: stamp.version,
        ato_ruleset_stamped_at: stamp.stampedAt,
      });
      toast.success("Saved to Approval Queue as pending");
    } catch (err) {
      devLog.warn("save pending failed", err);
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const verdict = result?.verdict || null;
  const meta = verdict ? VERDICT_META[verdict] || VERDICT_META.conditional : null;
  const Icon = meta?.icon;

  return (
    <section
      className="bi-surface p-4 space-y-3"
      data-testid="deduction-checker"
    >
      <div className="flex items-center gap-2 text-[#FF9500]">
        <Search className="w-4 h-4" />
        <span className="text-[10px] uppercase tracking-[0.2em] font-heading">Deduction Checker</span>
      </div>
      <div className="font-heading font-extrabold text-lg leading-tight">
        Is this deductible?
      </div>
      <div className="text-xs text-white/50 -mt-1">
        Punch in a quick summary — or snap the receipt / speak it — we&apos;ll cross-check against the ATO ruleset.
      </div>

      <div className="grid grid-cols-2 gap-2">
        <input
          ref={photoInput}
          type="file"
          accept="image/*"
          onChange={onPhoto}
          className="hidden"
          data-testid="dc-photo-input"
        />
        <button
          type="button"
          data-testid="dc-photo-btn"
          onClick={() => photoInput.current?.click()}
          disabled={busy || recording}
          className="h-11 rounded-xl bi-ghost-btn text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
        >
          <Camera className="w-4 h-4" /> Snap receipt
        </button>
        <button
          type="button"
          data-testid="dc-voice-btn"
          onClick={recording ? stopVoice : startVoice}
          disabled={busy && !recording}
          className={`h-11 rounded-xl text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-50 ${
            recording ? "bg-[#F87171] text-[#111827] font-semibold" : "bi-ghost-btn"
          }`}
        >
          {recording ? (
            <>
              <Square className="w-4 h-4" fill="currentColor" /> Stop &amp; check
            </>
          ) : (
            <>
              <Mic className="w-4 h-4" /> Ask by voice
            </>
          )}
        </button>
      </div>

      {(previewImage || transcript) && (
        <div className="bi-surface-2 p-2 rounded-lg flex items-start gap-2" data-testid="dc-preview">
          {previewImage && (
            <img
              src={previewImage}
              alt="preview"
              className="w-14 h-14 rounded object-cover shrink-0"
            />
          )}
          {transcript && (
            <div className="text-xs text-white/70 leading-snug flex-1">
              <span className="text-white/40 text-[10px] uppercase font-heading tracking-wider block">
                You said
              </span>
              &ldquo;{transcript}&rdquo;
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <input
          data-testid="dc-supplier"
          className="bi-input col-span-1"
          placeholder="Supplier"
          value={form.supplier}
          onChange={(e) => setForm({ ...form, supplier: e.target.value })}
        />
        <input
          data-testid="dc-total"
          type="number"
          className="bi-input col-span-1"
          placeholder="Total $"
          value={form.total || ""}
          onChange={(e) => setForm({ ...form, total: parseFloat(e.target.value) || 0 })}
        />
        <input
          data-testid="dc-description"
          className="bi-input col-span-2"
          placeholder="What is it? e.g. cordless drill, coffee with client, ute service"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
        <BiSelect
          data-testid="dc-category"
          className="bi-input col-span-2"
          value={form.deduction_category}
          onChange={(e) => setForm({ ...form, deduction_category: e.target.value })}
        >
          <option value="materials">Materials</option>
          <option value="equipment">Tools & Equipment</option>
          <option value="vehicle">Fuel & Vehicle</option>
          <option value="subcontractor">Subcontractor</option>
          <option value="site">Site Costs</option>
          <option value="safety">PPE & Safety</option>
          <option value="admin">Admin & Office</option>
          <option value="travel">Meals & Travel</option>
          <option value="home_office">Home Office</option>
        </BiSelect>
      </div>

      <button
        type="button"
        data-testid="dc-run"
        onClick={run}
        disabled={busy || (!form.supplier && !form.description)}
        className="w-full h-12 rounded-xl bi-accent-btn flex items-center justify-center gap-2 disabled:opacity-60 shadow-[0_10px_30px_-15px_rgba(255,149,0,0.6)]"
      >
        {busy ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Checking…
          </>
        ) : (
          <>
            <Search className="w-4 h-4" /> Check deductibility
          </>
        )}
      </button>

      {result && meta && (
        <div
          data-testid="dc-result"
          className="rounded-xl p-3 border"
          style={{
            borderColor: `${meta.color}55`,
            background: `${meta.color}0d`,
          }}
        >
          <div className="flex items-center gap-2">
            <Icon className="w-5 h-5" style={{ color: meta.color }} />
            <div className="font-heading font-extrabold text-lg" style={{ color: meta.color }}>
              {meta.label}
            </div>
            {result.percent !== undefined && result.percent < 100 && result.verdict !== "no" && (
              <span className="ml-auto text-xs text-white/70">
                up to {result.percent}%
              </span>
            )}
          </div>
          <div className="mt-2 text-sm text-white/85 leading-snug">{result.reason}</div>
          {result.ato_reference && (
            <div className="mt-2 text-[11px] text-white/50">
              Reference: <b className="text-white/70">{result.ato_reference}</b>
            </div>
          )}
          {result.strict_flags?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {result.strict_flags.map((f) => (
                <span
                  key={f}
                  className="text-[10px] px-2 py-0.5 rounded-full border border-[#F87171]/30 bg-[#F87171]/10 text-[#F87171]"
                >
                  ATO strict: {f}
                </span>
              ))}
            </div>
          )}
          {(previewImage || transcript || (form.supplier && Number(form.total) > 0)) &&
            result.verdict !== "no" && (
              <button
                type="button"
                data-testid="dc-save-pending"
                onClick={saveAsPending}
                disabled={saving}
                className="mt-3 w-full h-10 rounded-lg bi-accent-btn text-xs font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                Save to Approval Queue
              </button>
            )}
        </div>
      )}

      <TaxDisclaimer compact />
    </section>
  );
}
